import type { ClientLoaderFunctionArgs } from '@remix-run/react';
import * as Sentry from '@sentry/remix';
import type { NodeContent } from '~/interfaces/page';
import type { PricingDetails, Subscription } from '~/interfaces/subscriptions';
import type { StaticPageLoaderData } from '~/services/static_page';
import { fetchPricingDetails } from '~/utils/fetchPricingDetails';
import { findMultipleTipTapContentNodes } from '~/utils/findTipTapContentNode';
import getCookieValue from '~/utils/getCookieValue';
import { shouldClipAfterContentGate } from '~/utils/shouldClipAfterContentGate';

const staticPageClientLoader = async ({ request, serverLoader }: ClientLoaderFunctionArgs) => {
  const serverData = await serverLoader<StaticPageLoaderData>();
  const content = structuredClone(serverData?.page?.viewable_page_version?.content);
  if (!content) {
    return serverData;
  }
  const url = new URL(request.url);
  const searchParams = new URLSearchParams(url.search);

  let foundNodes = findMultipleTipTapContentNodes(content as NodeContent, ['pricing', 'contentGate']);
  // Look for pricing nodes in contentGate if present
  if (foundNodes.contentGate && !foundNodes.pricing && foundNodes.contentGate.attrs.popupContent) {
    foundNodes = {
      ...foundNodes,
      ...findMultipleTipTapContentNodes(foundNodes.contentGate.attrs.popupContent as NodeContent, ['pricing']),
    };
  }
  const hasFoundNodes = Object.values(foundNodes).some((node) => node !== null);

  if (!hasFoundNodes) {
    return serverData;
  }

  const requests = [];

  if (foundNodes.pricing) {
    requests.push(fetchPricingDetails());
  } else {
    requests.push(Promise.resolve(null));
  }

  const token = getCookieValue('token', document.cookie);
  if (foundNodes.contentGate && token) {
    const requestBody: { jwt_token: string; subscriber_id?: string } = { jwt_token: token };
    requests.push(
      fetch('/current_subscription', {
        method: 'POST',
        headers: {
          'Sec-Fetch-Dest': 'empty',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody),
      }),
    );
  }

  let pricingData: PricingDetails | undefined;
  let hasPricingDataFetchError = false;
  let subscription: Subscription | undefined;

  const [pricingRes, subscriptionRes] = await Promise.all(requests);

  if (pricingRes && 'tiers' in pricingRes) {
    pricingData = pricingRes;
  } else {
    hasPricingDataFetchError = true;
  }

  if (subscriptionRes && (subscriptionRes as Response).ok) {
    subscription = await (subscriptionRes as Response).json();
  }

  let shouldClipContent = false;
  try {
    if (foundNodes.contentGate) {
      shouldClipContent = shouldClipAfterContentGate({
        contentGateNode: foundNodes.contentGate,
        subscription,
        cookiesHeader: document.cookie,
        currentTierId: searchParams.get('current_tier_id'),
      });
    }
  } catch (error: unknown) {
    Sentry.captureException(error);
  }

  return {
    ...serverData,
    page: {
      ...serverData.page,
      viewable_page_version: {
        ...serverData.page?.viewable_page_version,
        content,
      },
    },
    pricingData,
    hasPricingDataFetchError,
    hasPricingDataFetched: true,
    isContentClipped: shouldClipContent,
  };
};

staticPageClientLoader.hydrate = true;

export { staticPageClientLoader };
